import QuotaValidator from "Validators/Admin/Quota";
import { useEffect, useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";
import {
  currencyMask,
  defaultCurrencyMask,
  parseLocaleNumber,
} from "Helpers/Format";

import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";
import { useFormSchema } from "Hooks/useFormSchema";

import Button from "Components/Admin/Buttons";
import Input from "Components/Admin/Form/Input";
import Select from "Components/Admin/Form/Select";
import FormWrapper from "Components/Common/FormWrapper";

import * as S from "./styles";

const QuotaForm = ({
  projectId,
  selectedQuota,
  setSelectedQuota,
  getProjectQuotas,
  linkOptions,
  isSponsor,
}) => {
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();

  const [value, setValue] = useState("");

  const [selectedLink, setSelectedLink] = useState(null);

  useEffect(() => {
    if (!selectedQuota?.id) return;

    setValue(defaultCurrencyMask(selectedQuota?.value));

    // Aporte LEGADO preso a um vínculo direto (sem seletiva) não está entre as
    // opções — o campo fica vazio e o submit OMITE a chave, preservando o
    // vínculo atual (RN2 da parte 4). O destino desse legado é a pendência P8.
    setSelectedLink(
      linkOptions?.find(
        (option) => option.value === selectedQuota.project_sponsor_id,
      ) || null,
    );
  }, [selectedQuota, linkOptions]);

  const methods = useFormSchema(QuotaValidator, {
    defaultValues: {
      value: selectedQuota?.value,
    },
  });

  const { register, reset } = methods;

  const submit = async (data) => {
    if (isSponsor) return;

    setLoading(true);

    try {
      const route = selectedQuota?.id
        ? "/project-quotas/update-project-quota"
        : "/project-quotas/create-project-quota";

      data.value = parseLocaleNumber(value);

      await api.post(route, {
        ...data,
        project_quota_id: selectedQuota?.id,
        // Chave OMITIDA quando não há seletiva escolhida: é o estado "manter o
        // vínculo atual" do contrato de 3 estados. Mandar null desvincularia o
        // aporte legado que o campo não representa.
        ...(selectedLink?.value
          ? { project_sponsor_id: selectedLink.value }
          : {}),
        project_id: projectId,
      });

      setLoading(false);

      showNotification("success", "Sucesso!", "Cota atualizada com sucesso!");

      reset();
      setSelectedQuota(null);
      getProjectQuotas(projectId);
    } catch (error) {
      setLoading(false);
      return HTTPErrorCallback(error);
    }
  };

  return (
    <S.Wrapper>
      <S.Modal>
        <FormWrapper {...methods} submit={submit}>
          <S.Section>
            <span>{selectedQuota?.id ? "Atualizar" : "Cadastrar"}</span>
            <hr />
          </S.Section>

          {/* O campo Nome saiu (D6 da parte 2, 03/09/2026): o aporte se
              identifica por patrocinador + valor. A coluna segue no banco pelo
              histórico das linhas antigas. */}
          <Input
            {...register("value")}
            placeholder="Digite aqui"
            label="Valor:"
            type="text"
            value={value}
            onChange={(e) => setValue(currencyMask(e.target.value))}
          />
          {/* O campo Contrapartida saiu (D4, 24/09/2026): a contrapartida é
              cadastrada na sessão própria, pelo administrador. O texto livre
              `setbacks` das linhas antigas fica no banco como histórico e não
              é reexibido. */}

          {/* Era "Patrocinador" e vinha SEMPRE vazio: as opções eram usuários
              filtrados contra os vínculos DIRETOS do projeto, conjunto que o
              modelo por seletiva esvaziou — criar aporte pela UI era
              impossível. Agora o campo é a SELETIVA (D1) e o valor é o
              `project_sponsor_id`, único id não ambíguo do vínculo (D8).

              Obrigatório desde D7 da parte 2: aporte sem aportador não conta no
              KPI e não tem rótulo sob D6. */}
          <Select
            name="project_sponsor_id"
            label="Seletiva"
            options={linkOptions}
            placeholder="Selecione uma Seletiva"
            value={selectedLink}
            onChange={setSelectedLink}
            maxWidth="100%"
            required
          />

          <S.Buttons>
            <Button
              type="button"
              outline
              text="Fechar"
              disabled={loading}
              onClick={() => setSelectedQuota(null)}
            />

            <Button
              type="submit"
              text={selectedQuota?.id ? "Atualizar" : "Cadastrar"}
              disabled={loading || isSponsor}
            />
          </S.Buttons>
        </FormWrapper>
      </S.Modal>
    </S.Wrapper>
  );
};

export default QuotaForm;
